import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/Button";
import { Field, Input } from "@/components/Field";
import { Pill } from "@/components/Pill";
import { toast } from "@/components/Toast";
import { EmptyState } from "@/components/EmptyState";
import { AdminShell } from "./users";

function AdminInvitesPage() {
  const [email, setEmail] = useState("");
  const qc = useQueryClient();
  const invites = useQuery({
    queryKey: queryKeys.adminInvites("pending"),
    queryFn: () => api.adminListInvites("pending"),
    retry: false,
  });

  const create = useMutation({
    mutationFn: (e: string) => api.adminCreateInvite(e),
    onSuccess: (data) => {
      setEmail("");
      qc.invalidateQueries({ queryKey: queryKeys.adminInvites() });
      if (data.delivery.ok) {
        toast.success(`Invite sent to ${data.invite.email}.`);
      } else {
        toast.error(
          `Invite created but email delivery failed: ${data.delivery.error ?? "unknown"}. Use Resend below to retry.`,
        );
      }
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "user_exists") {
        toast.error("That email already has an account — no invite needed.");
      } else if (e instanceof ApiError && e.code === "validation") {
        toast.error("Enter a valid email address.");
      } else {
        toast.error("Couldn't create invite. Try again.");
      }
    },
  });

  const resend = useMutation({
    mutationFn: (id: string) => api.adminResendInvite(id),
    onSuccess: (data) => {
      if (data.delivery.ok) toast.success("Invite re-sent.");
      else
        toast.error(`Resend failed: ${data.delivery.error ?? "unknown error"}`);
    },
    onError: () => toast.error("Couldn't re-send invite."),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.adminRevokeInvite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminInvites() });
      toast.success("Invite revoked.");
    },
    onError: () => toast.error("Couldn't revoke."),
  });

  if (invites.isError) {
    return (
      <AdminShell tab="invites">
        <EmptyState
          icon="key"
          title="Admin access required."
          caption="Ask an admin to promote your account before opening this page."
        />
      </AdminShell>
    );
  }

  return (
    <AdminShell tab="invites">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = email.trim();
          if (!trimmed) return;
          create.mutate(trimmed);
        }}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)",
          padding: 16,
          marginBottom: 20,
          display: "grid",
          gap: 12,
        }}
      >
        <h3 className="t-h4" style={{ margin: 0 }}>
          Invite a teammate
        </h3>
        <p className="t-sm" style={{ margin: 0, opacity: 0.75 }}>
          They'll get an email with a sign-in link. Invites expire after 7 days.
        </p>
        <Field label="Email" id="invite-email">
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="teammate@example.com"
            required
          />
        </Field>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={create.isPending || !email.trim()}
          >
            {create.isPending ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </form>

      <h3 className="t-h4" style={{ margin: "0 0 10px" }}>
        Pending invites
      </h3>
      {invites.data && invites.data.invites.length === 0 ? (
        <EmptyState
          icon="mail"
          title="No pending invites."
          caption="Invite a teammate to join the workspace."
          actionLabel="New invite"
          onAction={() => {
            document.getElementById("invite-email")?.focus();
          }}
        />
      ) : (
        <div
          className="panel"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-lg)",
          }}
        >
          {invites.data?.invites.map((inv) => {
            const expiresMs = new Date(inv.expiresAt).getTime();
            const daysLeft = Math.max(
              0,
              Math.ceil((expiresMs - Date.now()) / (1000 * 60 * 60 * 24)),
            );
            return (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tr-title">{inv.email}</div>
                  <div className="t-xs" style={{ opacity: 0.7 }}>
                    Invited by{" "}
                    {inv.invitedByName?.trim() ||
                      inv.invitedByEmail ||
                      inv.invitedBy}
                  </div>
                </div>
                <Pill variant={daysLeft <= 1 ? "overdue" : "todo"} dot>
                  {daysLeft}d left
                </Pill>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => resend.mutate(inv.id)}
                  disabled={resend.isPending}
                >
                  Resend
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      confirm(
                        `Revoke invite for ${inv.email}? They won't be able to sign in unless re-invited.`,
                      )
                    ) {
                      revoke.mutate(inv.id);
                    }
                  }}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}

export const Route = createFileRoute("/admin/invites")({
  component: AdminInvitesPage,
});
